import { checkNever } from '../../../utils/checkNever.ts';
import { type IReference, ReferenceType } from '../references/IReference.ts';
import {
  type IReferencePathItem,
  ReferencePathDirection,
} from '../references/IReferencePath.ts';

/**
 * Converte un riferimento in un percorso valido per il `parentWhen` di yup.
 * Questi percorsi sono _relativi_
 *
 * NOTE: Se viene aggiunto un tipo NON di contesto ($), è necessario aggiornare
 * anche `mapReferenceToAbsolutePath` per gestire correttamente i path relativi.
 */
export function mapReferenceToPath(ref: IReference): string {
  switch (ref.type) {
    case ReferenceType.Sample:
      return `$sample.${ref.code}`;
    // Da mantenere sempre allineato con:
    // 1) `registerToReference` (non così importante il `path` per ora, non viene usato qui)
    // 2) `getValidationContext`
    case ReferenceType.Register:
      return `$registers.${ref.code}`;
    case ReferenceType.Widget:
      return createWidgetReferencePath(ref);
    case ReferenceType.Session:
      return mapSessionReferenceToPath(ref);
    case ReferenceType.VirtualColumn:
      return `$virtualColumns.${ref.code}`;
    case ReferenceType.SemanticFields:
      return `$semanticFields.${ref.code}`;
    case ReferenceType.DossierSemanticFields:
      return `$dossierSemanticFields.${ref.code}`;
    case ReferenceType.Metadata:
      return mapContextReferenceToPath(ref);
    case ReferenceType.Placeholder:
      return mapContextReferenceToPath(ref);
    default:
      console.error(`[MapReferenceToPath] Unsupported reference type`, { ref });
      checkNever(ref.type);
      return '<unknown>';
  }
}

function createWidgetReferencePath(reference: IReference): string {
  let result = '';
  for (const item of reference.path) {
    const segment = parseWidgetPath(item);
    if (segment != null) {
      result = result ? `${result}.${segment}` : segment;
    }
  }
  return result ? `${result}.${reference.code}` : reference.code;
}

// Il funzionamento è uguale a mapContextReferenceToPath, ma dobbiamo
// gestire il caso di rootPath senza direction assoluta per
// retrocompatibilità
export function mapSessionReferenceToPath(ref: IReference): string {
  const [rootPath, ...otherPaths] = ref.path ?? [];
  if (
    rootPath?.direction === ReferencePathDirection.Absolute &&
    rootPath?.code === 'session' &&
    otherPaths.length > 0
  ) {
    const pathTokens = otherPaths.map(p => p.code).join('.');
    return `$session.${pathTokens}.${ref.code}`;
  }

  return `$session.${ref.code}`;
}

export function mapContextReferenceToPath(ref: IReference): string {
  if (ref.path[0]?.direction !== ReferencePathDirection.Absolute) {
    // biome-ignore format: <tbd>
    console.warn(`[MapContextReferenceToPath] Expected absolute path for context reference`, { ref });
  }
  const pathTokens = ref.path.map(p => p.code).join('.');
  return `$${pathTokens}.${ref.code}`;
}

function parseWidgetPath(path: IReferencePathItem) {
  if (path.direction === ReferencePathDirection.Parent) {
    return `^`;
  }

  if (path.direction === ReferencePathDirection.Root) {
    return `^.${path.code}`;
  }

  return path.code;
}
